import React, { FC, ReactNode } from 'react';
import styles from './contact-block.module.scss';
import InfoField from '../info-field/info-field';
import { ReactComponent as EmailIcon } from 'assets/icons/email.svg';
import { IContactBlockProps } from './contact-block.types';
import dayjs from 'dayjs';
import { ResidentTypesEnum } from 'common/enums/resident-types.enum';
import { getVacationPeriod } from '../../../helpers/vacation.helper';
import { NotificationStatuses } from './NotificationStatuses/NotificationStatuses';
import { IRelatedResidentRead } from 'common/services/residents-service/residents.service.types';
import { isRentExpired } from 'common/helpers/date.helper';
import Feature from 'common/components/feature/feature';
import { FeatureName } from 'common/enums/feature-name.enum';
import { ResidenceStatus } from 'common/enums/residence-status.enum';
import { residenceStatusMap } from 'common/constans/common.constants';
import ViewPhoto from 'common/components/view-photo/view-photo';
import featureStore from 'common/stores/features/feature.store';

const ContactBlock: FC<IContactBlockProps> = ({
  email,
  clubId,
  pin,
  legacyCode,
  vacationStartDate,
  vacationEndDate,
  rentStatrtDate,
  rentEndDate,
  ownershipType,
  relatedTo,
  notificaitonChannels,
  clubStatus,
  status,
  comment,
  photoUrl,
  agencyName,
  licenseNumber,
}) => {
  const GetVacationField = (startDateString: string | null, endDateString: string | null): ReactNode => {
    const isVacation = startDateString && endDateString && dayjs().isBetween(startDateString, endDateString, 'day', '[]');

    if (!isVacation) {
      return <InfoField value={'Home'} label="Vacation" valueClassName="eiq-label primary" bodyClassName={styles.infoBlockField} />;
    } else {
      return <InfoField value={'Vacationing'} label="Vacation" valueClassName="eiq-label warning" bodyClassName={styles.infoBlockField} />;
    }
  };

  const isShowRentalDates = (ownershipType?: ResidentTypesEnum, relatedTo?: IRelatedResidentRead | null) => {
    return ownershipType === ResidentTypesEnum.Renter || relatedTo?.ownershipType === ResidentTypesEnum.Renter;
  };

  const getRentalPeriod = (rentStartDate?: string | null, rentEndDate?: string | null, relatedTo?: IRelatedResidentRead | null) => {
    const start = rentStartDate ?? relatedTo?.rentStartDate;
    const end = rentEndDate ?? relatedTo?.rentEndDate;

    if (!start || !end) {
      return 'Unknown';
    }

    return getVacationPeriod(start, end);
  };

  return (
    <>
      {relatedTo && (
        <div className={`${styles.infoBlockContainer} ${styles.relatedToContainer}`}>
          <InfoField label="Related to" value={`${relatedTo.firstName} ${relatedTo.lastName}`} bodyClassName={styles.relatedTo} />
        </div>
      )}
      <div className={styles.infoBlockContainer}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <div>
            {isShowRentalDates(ownershipType, relatedTo) && (
              <div className={styles.rentalDates}>
                <span>
                  <span className={styles.rentalDateTitle}>Rental Dates:</span>
                  <span
                    className={
                      isRentExpired(rentStatrtDate ?? relatedTo?.rentStartDate, rentEndDate ?? relatedTo?.rentEndDate) ? styles.red : ''
                    }>
                    {` ${getRentalPeriod(rentStatrtDate, rentEndDate, relatedTo)}`}
                  </span>
                </span>
              </div>
            )}
            <div className={styles.infoLine}>
              <InfoField value={email} label="Email address" bodyClassName={styles.infoBlockField} valueIcon={<EmailIcon />} />
            </div>
          </div>
          {featureStore.isEIQProfileEnabled && <div>{photoUrl && <ViewPhoto photoUrl={photoUrl} />}</div>}
        </div>

        <div className={styles.infoLine}>
          <InfoField
            value={<NotificationStatuses channels={notificaitonChannels} />}
            label="Communication Consent"
            bodyClassName={styles.infoBlockField}
            valueClassName={styles.communicationConsent}
          />
        </div>
        <Feature name={FeatureName.RentalPortal}>
          {ownershipType === ResidentTypesEnum.RentalAgency && (
            <div className={`${styles.infoLine} ${styles.clubIdContainer}`}>
              <InfoField value={agencyName} label="Agency name" bodyClassName={styles.infoBlockField} />
              <InfoField value={licenseNumber} label="License number" bodyClassName={styles.infoBlockField} />
            </div>
          )}
        </Feature>
        <div className={`${styles.infoLine} ${styles.clubIdContainer}`}>
          <InfoField value={clubId} label="Club ID" bodyClassName={styles.infoBlockField} />
          <div className={styles.infoBlockField}>
            <InfoField value={pin} label="PIN" />
            {legacyCode && <InfoField value={legacyCode} label="Legacy Code" bodyClassName={styles.legacyCode} />}
          </div>
          <Feature name={FeatureName.Jonas}>
            <InfoField value={clubStatus} label="Club Status" bodyClassName={styles.infoBlockField} />
          </Feature>
          <Feature name={FeatureName.NorthStar}>
            {status === ResidenceStatus.MovedOut && (
              <InfoField value={residenceStatusMap[status]} label="Residence Status" bodyClassName={styles.infoBlockField} />
            )}
          </Feature>
        </div>
        {ownershipType !== ResidentTypesEnum.UnderAgeChild && ownershipType !== ResidentTypesEnum.AdultChild && (
          <div className={`${styles.infoLine} ${styles.vacationContainer}`}>
            {GetVacationField(vacationStartDate, vacationEndDate)}
            <InfoField
              value={getVacationPeriod(vacationStartDate, vacationEndDate) ?? 'Not available'}
              label="Vacation dates"
              bodyClassName={styles.infoBlockField}
            />
          </div>
        )}
        {comment && <InfoField value={comment} label="Comments" bodyClassName={`${styles.infoBlockField} ${styles.comments}`} />}
      </div>
    </>
  );
};

export default ContactBlock;
